import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ApiMethods from "../../../../ApiManager/ApiMethods";
import { useAlert } from "../../../context/AlertContext";
import useLockBodyScroll from "../../../hooks/useLockBodyScroll";
import { HOUR_ROWS, WEEK_DAY_LABELS, cellKey } from "./termini-shared";

const HOURS_ONLY = HOUR_ROWS.filter((row) => row !== "break");

// "Copy Previous Week" — pulls the immediately-previous week's slot configuration and re-creates
// whatever's missing in the currently selected week. Additive-only: never deletes/replaces an
// existing slot in the target week (soft-deleting a slot with real bookings would orphan those
// bookings from capacity accounting — see this app's `cal_timeslots`/`view_timeslot` design), so
// "if the target week already has slots" only ever produces a warning, not a destructive overwrite.
const CopyWeekModal = ({
  open,
  weekDates,
  weekLabel,
  timeslotsByStart,
  onClose,
  onSaved,
}) => {
  const { showAlert } = useAlert();
  useLockBodyScroll(open);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [prevByDay, setPrevByDay] = useState({});

  const prevWeekDates = useMemo(
    () => weekDates.map((d) => moment(d).subtract(7, "days").toDate()),
    [weekDates]
  );
  const prevWeekLabel = weekDates.length
    ? `${prevWeekDates[0].getDate()} ${moment(prevWeekDates[0]).format(
        "MMM"
      )} - ${prevWeekDates[prevWeekDates.length - 1].getDate()} ${moment(
        prevWeekDates[prevWeekDates.length - 1]
      ).format("MMM")}`
    : "";

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const startDate = moment(prevWeekDates[0]).format("YYYY-MM-DD");
    ApiMethods.get(`timeslots?start=${startDate}`)
      .then((res) => {
        const byDay = {};
        prevWeekDates.forEach((date, idx) => {
          const set = new Set();
          HOURS_ONLY.forEach((hour) => {
            const key = cellKey(date, hour);
            const exists = (res || []).some(
              (ts) =>
                moment.parseZone(ts.start).format("YYYY-MM-DD HH:mm:ss") ===
                key
            );
            if (exists) set.add(hour);
          });
          byDay[idx] = set;
        });
        setPrevByDay(byDay);
      })
      .catch((err) => {
        console.log(err);
        showAlert("Greška pri učitavanju prethodne nedelje", "error");
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const newSlotsCount = useMemo(() => {
    let count = 0;
    weekDates.forEach((date, idx) => {
      (prevByDay[idx] || new Set()).forEach((hour) => {
        if (!timeslotsByStart[cellKey(date, hour)]) count += 1;
      });
    });
    return count;
  }, [prevByDay, timeslotsByStart, weekDates]);

  // Desktop only, per spec — Mobile relies on the X/"Otkaži" tap targets instead.
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape" && window.innerWidth >= 768) onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  const targetAlreadyHasSlots = Object.keys(timeslotsByStart).length > 0;

  const previewRows = weekDates
    .map((date, idx) => ({
      idx,
      hours: Array.from(prevByDay[idx] || new Set()).sort((a, b) => a - b),
    }))
    .filter((row) => row.hours.length > 0);

  const handleCopy = async () => {
    if (newSlotsCount === 0) {
      onClose();
      return;
    }
    setSaving(true);
    try {
      const timeslots = [];
      weekDates.forEach((date, idx) => {
        (prevByDay[idx] || new Set()).forEach((hour) => {
          const key = cellKey(date, hour);
          if (!timeslotsByStart[key]) {
            timeslots.push({
              start: key,
              end: moment(key, "YYYY-MM-DD HH:mm:ss")
                .add(1, "hour")
                .format("YYYY-MM-DD HH:mm:ss"),
            });
          }
        });
      });
      await ApiMethods.post("timeslots/create", { timeslots });
      showAlert("Raspored prethodne nedelje je kopiran", "success");
      onSaved();
    } catch (err) {
      console.log(err);
      showAlert("Greška prilikom kopiranja rasporeda", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="hf-tm-emp-overlay">
      <div className="hf-tm-copy-modal">
        <div className="hf-tm-week-header">
          <div>
            <h2 className="hf-tm-week-title">Kopiraj prethodnu nedelju?</h2>
            <p className="hf-tm-week-subtitle">
              Raspored termina od {prevWeekLabel} biće kopiran na {weekLabel}.
            </p>
          </div>
          <button
            type="button"
            className="hf-tm-week-close"
            onClick={onClose}
            aria-label="Zatvori"
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </button>
        </div>

        {loading ? (
          <div className="hf-tm-copy-loading">Učitavanje...</div>
        ) : (
          <>
            {targetAlreadyHasSlots && (
              <div className="hf-tm-copy-warning">
                <WarningAmberOutlinedIcon sx={{ fontSize: 17 }} />
                <span>
                  Ova nedelja već sadrži podešene termine. Postojeći termini
                  ostaju netaknuti — kopiraju se samo termini kojih još nema.
                </span>
              </div>
            )}

            <div className="hf-tm-copy-preview">
              <div className="hf-tm-copy-preview-col">
                <span className="hf-tm-copy-preview-label">
                  Prethodna nedelja
                </span>
                <span className="hf-tm-copy-preview-range">
                  {prevWeekLabel}
                </span>
                {previewRows.length === 0 ? (
                  <p className="hf-tm-copy-preview-empty">
                    Nema podešenih termina.
                  </p>
                ) : (
                  <div className="hf-tm-copy-preview-rows">
                    {previewRows.map((row) => (
                      <div key={row.idx} className="hf-tm-copy-preview-row">
                        <span className="hf-tm-copy-preview-day">
                          {WEEK_DAY_LABELS[row.idx]}
                        </span>
                        <span className="hf-tm-copy-preview-hours">
                          {row.hours.map((h) => `${h}:00`).join(", ")}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <ArrowForwardIcon
                className="hf-tm-copy-preview-arrow"
                sx={{ fontSize: 18 }}
              />

              <div className="hf-tm-copy-preview-col">
                <span className="hf-tm-copy-preview-label">
                  Izabrana nedelja
                </span>
                <span className="hf-tm-copy-preview-range">{weekLabel}</span>
                <p className="hf-tm-copy-preview-empty">
                  {newSlotsCount > 0
                    ? `Dodaje se ${newSlotsCount} novih termina.`
                    : "Nema novih termina za dodavanje."}
                </p>
              </div>
            </div>
          </>
        )}

        <div className="hf-tm-week-footer">
          <span />
          <div className="hf-tm-week-footer-actions">
            <button
              type="button"
              className="hf-tm-emp-cancel"
              onClick={onClose}
              disabled={saving}
            >
              Otkaži
            </button>
            <button
              type="button"
              className="hf-tm-emp-confirm"
              disabled={saving || loading || newSlotsCount === 0}
              onClick={handleCopy}
            >
              {saving ? "Kopiranje..." : "Kopiraj raspored"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CopyWeekModal;
